<template>
  <!-- New Note Input -->
  <div class="flex items-start mb-6">
    <Avatar
      :url="authStore.details.profilePicUrl"
      :text="authStore.details.initials"
      size="xl"
    />
    <form
      ref="form"
      class="flex items-baseline ml-5 w-full"
      @submit.prevent="submitNewNote"
    >
      <div class="w-full">
        <TextArea
          v-model="newNote.content"
          rows="1"
          auto-resize
          class="w-full"
          style="max-height: 150px !important"
          placeholder="Type a new note..."
          has-speech-to-text
          :has-error="newNoteV$.content.$error"
        />
        <InputError :has-error="newNoteV$.content.$error">{{ newNoteV$.content.$errors[0].$message }}</InputError>
      </div>
      <Button
        type="submit"
        class="ml-6"
        icon="check"
        :is-loading="newNoteApiRequest.isLoading.value"
      />
    </form>
  </div>

  <Timeline v-if="items">
    <TimelineItem
      v-for="item in items"
      :key="item"
      :title="item.creator?.name"
      :timestamp="item.timestamp"
      :avatar-url="item.creator?.profilePicUrl"
      :avatar-icon="(item.creator?.id == null && 'robot') || (item.creator?.type == 'customer' && 'user')"
      :avatar-text="item.creator?.initials"
    >
      <template #content>
        <div class="flex gap-1 items-start">
          <Icon
            :type="item.timelineType.icon"
            class="mr-1"
          />
          <div>
            <span>{{ item.timelineType.descriptor?.prepend }}</span>
            <router-link
              v-if="item.subjectLink"
              class="link"
              :to="item.subjectLink"
            >
              {{ item.timelineType.descriptor?.subject }}
            </router-link>
            <span v-else>{{ item.timelineType.descriptor?.subject }}</span>
            <span>{{ item.timelineType.descriptor?.append }}</span>
            <span
              v-if="item.type === 'contact_email' && item.text"
              class="ml-1 text-quaternary"
            >
              ({{ item.text }})
            </span>
            <span
              v-if="item.type === 'contact_call' && item.text"
              class="ml-1 text-quaternary"
            >
              ({{ item.text }})
            </span>
            <span
              v-if="item.type === 'appointment_booked' && item.text"
              class="text-quaternary"
            >
              for {{ item.text }}
            </span>
          </div>
        </div>
        <div class="flex gap-2 items-center">
          <MessageBubble
            v-if="item.timelineType.inMessageBubble"
            class="inline-block"
          >
            <CopyText v-if="item.type == 'note'">
              {{ item.text }}
            </CopyText>
            <div v-else>{{ item.text }}</div>
          </MessageBubble>
          <div
            v-if="
              item.type == 'note' &&
              dayjs().diff(dayjs(item.timestamp), 'minutes', true) < 5 &&
              item.creator?.id == authStore.details.id
            "
            class="mt-2"
          >
            <Button
              icon="pencil"
              plain
              severity="secondary"
              @click="editNote(item)"
            />
          </div>
        </div>
      </template>
    </TimelineItem>
  </Timeline>
</template>

<script setup>
import { ref } from 'vue'

import { useAuthStore } from '@/stores/AuthStore'

import useVuelidate from '@vuelidate/core'
import { required, helpers } from '@vuelidate/validators'

import Avatar from '@/components/avatar/Avatar.vue'
import Icon from '@/components/icon/Icon.vue'
import TextArea from '@/components/forms/TextArea.vue'
import InputError from '@/components/forms/InputError.vue'
import Timeline from '@/components/timeline/Timeline.vue'
import TimelineItem from '@/components/timeline/TimelineItem.vue'
import MessageBubble from '@/components/message/MessageBubble.vue'
import CopyText from '@/components/CopyText.vue'
import Button from '@/components/button/Button.vue'
import useApiRequest from '@/composables/useApiRequest'

import dayjs from 'dayjs'
import { alertToast } from '@/utilities/notification'
import { getDictionaryItem } from '@/dictionary'

const authStore = useAuthStore()

const props = defineProps({
  agreementID: {
    type: [String, Number],
    required: true
  }
})

const items = defineModel('items', {
  type: Array,
  default: () => []
})

const emit = defineEmits(['note-added', 'edit-note'])

const newNoteApiRequest = useApiRequest()

const newNote = ref({
  userID: authStore.details.id,
  content: '',
  hasError: false,
  isLoading: false
})

const newNoteRules = {
  userID: { required },
  content: { required: helpers.withMessage('Enter a note', required) },
  hasError: { required },
  isSubmitting: false
}

const newNoteV$ = useVuelidate(newNoteRules, newNote, { $stopPropagation: true })

/**
 * Submits a new note to the API and updates the timeline
 */
async function submitNewNote() {
  const isFormCorrect = await newNoteV$.value.$validate()

  if (!isFormCorrect) {
    return
  }

  try {
    const response = await newNoteApiRequest.send({
      endpoint: `/v1/agreements/${props.agreementID}/notes`,
      method: 'POST',
      data: JSON.stringify({
        content: newNote.value.content
      })
    })

    if (!response.success) {
      alertToast('Failed to add note', response.data?.message, 'danger')
      return
    }

    const itemType = getDictionaryItem('agreementTimelineTypes', 'note')

    const newTimelineItem = {
      type: 'note',
      resourceId: response.data.id,
      timestamp: dayjs().toISOString(),
      text: newNote.value.content,
      creator: {
        id: authStore.details.id,
        user_type: authStore.details.userType,
        name: authStore.details.name,
        profilePicUrl: authStore.details.profilePicUrl,
        initials: authStore.details.initials
      },
      timelineType: itemType
    }

    // Add to the beginning of the timeline
    items.value.unshift(newTimelineItem)

    // Reset form
    newNote.value.content = ''
    newNoteV$.value.$reset()

    // Emit event to parent
    emit('note-added', newTimelineItem)

    alertToast('Note added', null, 'success')
  } catch (err) {
    alertToast('Failed to add note', err.message, 'danger')
    console.error(err)
  }
}

/**
 * Handles edit note click - emits event to parent
 */
function editNote(item) {
  emit('edit-note', item)
}
</script>

<style></style>
